import type { Component, JSX } from 'solid-js';
import { Show } from 'solid-js';

type ActionScreenProps = {
  eyebrow?: string;
  title: string;
  onBack: () => void;
  showBack?: boolean;
  children: JSX.Element;
  footer?: JSX.Element;
  /** Optional context marker used by child-specific guardian screens. */
  headerAvatar?: JSX.Element;
  class?: string;
};

/**
 * The shared Claude Design action-screen frame.
 *
 * Action screens are pushed above a destination screen: they have a compact
 * back header, a scrollable task body, and (where relevant) one footer CTA.
 * Keeping this as a component prevents the old settings/form shells from
 * drifting back into individual task flows.
 */
export const ActionScreen: Component<ActionScreenProps> = (props) => (
  <main
    class={`action-screen ${props.footer ? 'action-screen--has-footer' : 'action-screen--no-footer'}${props.class ? ` ${props.class}` : ''}`}
    data-testid="action-screen"
  >
    <header class="action-screen-header" data-testid="action-screen-header">
      <Show when={props.showBack !== false}>
        <button type="button" class="action-screen-back" onClick={props.onBack} aria-label="Back">
          <span aria-hidden="true">←</span>
        </button>
      </Show>
      <Show when={props.headerAvatar}>
        {(avatar) => (
          <span class="action-screen-header-avatar" aria-hidden="true">
            {avatar()}
          </span>
        )}
      </Show>
      <div class="action-screen-heading">
        <Show when={props.eyebrow}>
          {(eyebrow) => <span class="action-screen-eyebrow">{eyebrow()}</span>}
        </Show>
        <h1>{props.title}</h1>
      </div>
    </header>
    <section class="action-screen-body" data-testid="action-screen-body">
      {props.children}
    </section>
    <Show when={props.footer}>
      {(footer) => (
        <footer class="action-screen-footer" data-testid="action-screen-footer">
          {footer()}
        </footer>
      )}
    </Show>
  </main>
);
